import { Price } from '@shared-logic/prices';
import { WAY_PAY_VOID, wayPayNames } from './way-pay.constants';
import { WayPay } from './way-pay.interfaces';
import { getWayPayDefault } from './way-pay.service';

/**
 * WayPay template derived from a proforma payment code (IBM table CER050T5, field PAGO).
 * Days and expiration are not part of the template: days come from the proforma (paymentDays)
 * and the expiration is completed by the user when the payment method requires it.
 */
export interface WayPayTemplate {
    wayPayName: number;
    percentage: number;
    isBusinessDays: number; // 1 = business days, 0 = calendar days
    options: number; // 0 = none, 1 = deposit certificate, 2 = warrant
}

/**
 * Proforma payment code (CER050T5) -> our WayPay structure.
 *
 * Only the payment codes present in the proformas collection that have a conversion are mapped.
 * The IBM code sent to IBM is always the one returned by getWayPayCodeIBM for this structure,
 * not the CERCPA code that CER050T5 associates with the proforma code.
 * A proforma code that is not in this table has no conversion: no payment method is assigned
 * (the mother contract's payment method is never used as a default).
 *
 * Source of the table: scripts/proforma-way-pay-table (snapshot of the proformas collection).
 */
export const PROFORMA_WAY_PAY_TEMPLATES: Record<number, WayPayTemplate> = {
    // 2 - "97,5 % PAGO CONTRA ENTREGA" | CERCPA 212 | sends 212
    2: { wayPayName: wayPayNames.contraEntrega, percentage: 97.5, isBusinessDays: 1, options: 0 },

    // 12 - "PAGO X DIAS HABI D LA FIJA, EXPORTACION" | CERCPA 219 | sends 218 (user decision)
    12: { wayPayName: wayPayNames.contraFixMercaderiaEntregada, percentage: 97.5, isBusinessDays: 1, options: 0 },

    // 17 - "100% CONTRA FIJACION" | CERCPA 149 | sends 49 (user decision)
    17: { wayPayName: wayPayNames.contraFixMercaderiaEntregada, percentage: 100, isBusinessDays: 0, options: 0 },

    // 18 - "100% CDO C/ENTREGA" | CERCPA 15 | sends 15
    18: { wayPayName: wayPayNames.contraEntrega, percentage: 100, isBusinessDays: 1, options: 0 },

    // 19 - "97,5% CONTRA FIJACION" | CERCPA 219 | sends 218 (user decision)
    19: { wayPayName: wayPayNames.contraFixMercaderiaEntregada, percentage: 97.5, isBusinessDays: 1, options: 0 },

    // 37 - "100 % A LOS X DIAS DE LA FACTURA" | CERCPA 7 | sends 7
    37: { wayPayName: wayPayNames.diferidoFacturacion, percentage: 100, isBusinessDays: 1, options: 0 },

    // 44 - "95% CONTRA ENTREGA" | CERCPA 117 | sends 117
    44: { wayPayName: wayPayNames.contraEntrega, percentage: 95, isBusinessDays: 1, options: 0 },

    // 60 - "95% A LAS X HS DE LA FIJACION" | CERCPA 99 | sends 118 (user decision)
    60: { wayPayName: wayPayNames.contraFixMercaderiaEntregada, percentage: 95, isBusinessDays: 1, options: 0 },
};

/**
 * Returns the WayPay template for a proforma payment code, or undefined when the code has no conversion.
 */
export function getProformaWayPayTemplate(paymentCode: number): WayPayTemplate | undefined {
    return PROFORMA_WAY_PAY_TEMPLATES[paymentCode];
}

/**
 * Payment terms of a proforma that drive the exchange payment method.
 * Structurally compatible with the frontend proforma model.
 */
export interface ExchangeProformaPaymentTerms {
    paymentCode: number;
    paymentDays: number;
    inheritPaymentTerms: boolean;
}

/**
 * Builds the payment method of an exchange from the proforma of its buyer.
 * - No proforma: no payment method (WAY_PAY_VOID).
 * - inheritPaymentTerms: a copy of the mother contract payment method.
 * - Payment code with a template: the template with the proforma days, or the default days
 *   of that payment method when the proforma has none.
 * - Payment code without conversion: no payment method (WAY_PAY_VOID).
 * The mother contract payment method is never used as a default.
 * @param terms Proforma payment terms, undefined when the buyer has no proforma.
 * @param motherWayPay Payment method of the mother contract.
 * @param motherPrice Price of the mother contract, used to resolve default days.
 * @returns WayPay A new object, never shared with the inputs.
 */
export function getExchangeWayPayFromProforma(
    terms: ExchangeProformaPaymentTerms | undefined,
    motherWayPay: WayPay,
    motherPrice: Price,
): WayPay {
    if (!terms) {
        return { ...WAY_PAY_VOID };
    }

    if (terms.inheritPaymentTerms) {
        return { ...motherWayPay };
    }

    const template = getProformaWayPayTemplate(terms.paymentCode);
    if (!template) {
        return { ...WAY_PAY_VOID };
    }

    const days = terms.paymentDays > 0
        ? terms.paymentDays
        : getWayPayDefault(motherPrice, template.wayPayName).days;

    return {
        wayPayName: template.wayPayName,
        percentage: template.percentage,
        isBusinessDays: template.isBusinessDays,
        options: template.options,
        days,
        expiration: '--',
    };
}
